import {
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  Eye,
  EyeOff,
  Lock,
  Mail,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { formatApiError } from "../lib/api";

// Marketing tiles on the left panel — rotated every few seconds so the
// brand area feels alive without distracting from the form on the right.
// Each role gets a tailored value-prop, but all admins see the same rotation
// (we can't tell role until they sign in).
const HIGHLIGHTS = [
  {
    icon: ClipboardList,
    eyebrow: "Workflow",
    title: "One pane for every order",
    body: "Track each step from intake to delivery — drafts, payments, meetings, deliverables, all in one place.",
  },
  {
    icon: Users,
    eyebrow: "Team",
    title: "CA, Admin, Super Admin",
    body: "Role-aware access. Every action attributed, every customer routed to the right hands.",
  },
  {
    icon: ShieldCheck,
    eyebrow: "Audit",
    title: "Every action logged",
    body: "Per-user audit trail with IP and user-agent. Compliance built in, not bolted on.",
  },
  {
    icon: TrendingUp,
    eyebrow: "Revenue",
    title: "Recover what you'd lose",
    body: "Stalled funnel alerts surface drop-offs before they cost you a client.",
  },
];

export default function SignInPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(0);
  const { login } = useAuth();

  useEffect(() => {
    const id = setInterval(() => {
      setHighlightIdx((i) => (i + 1) % HIGHLIGHTS.length);
    }, 4500);
    return () => clearInterval(id);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      // Show a brief success confirmation before handing off to the app —
      // gives the redirect a moment to feel intentional rather than abrupt.
      setSuccess(true);
      setTimeout(() => window.location.replace("/admin"), 1100);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail ? formatApiError(detail) : err.message || "Sign in failed.");
      setLoading(false);
    }
  };

  const highlight = HIGHLIGHTS[highlightIdx];
  const HighlightIcon = highlight.icon;

  return (
    <div className="min-h-screen bg-[#F5F4FB] flex">
      {/* ─── Left: brand panel ──────────────────────────────────────────── */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-[#1A1A2E] via-[#2A1F5C] to-[#4F46E5] text-white">
        {/* Decorative blobs */}
        <div className="absolute -top-40 -left-32 w-[420px] h-[420px] bg-[#6C63FF]/30 rounded-full blur-3xl" />
        <div className="absolute -bottom-44 -right-20 w-[480px] h-[480px] bg-[#A78BFA]/25 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/3 w-[320px] h-[320px] bg-[#22D3EE]/10 rounded-full blur-3xl" />

        {/* Subtle grid texture */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "linear-gradient(white 1px, transparent 1px), linear-gradient(90deg, white 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />

        <div className="relative z-10 flex flex-col justify-between p-12 xl:p-16 w-full">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="bg-white/95 rounded-xl px-3 py-2 shadow-lg">
              <img
                src="/myca-logo.png"
                alt="myCA"
                className="h-9 w-auto object-contain"
              />
            </div>
            <span className="text-xs font-body uppercase tracking-[0.25em] text-white/60">
              Admin Panel
            </span>
          </div>

          {/* Heading + rotating highlight card */}
          <div className="space-y-8 max-w-lg">
            <div>
              <div className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur-sm border border-white/15 rounded-full px-3 py-1 mb-5">
                <Sparkles className="h-3 w-3 text-[#FBBF24]" />
                <span className="text-[11px] font-body font-medium tracking-wide text-white/80">
                  Built for Chartered Accountants
                </span>
              </div>
              <h1 className="text-4xl xl:text-5xl font-heading font-bold leading-[1.1]">
                Run your firm
                <br />
                like a product.
              </h1>
              <p className="text-base text-white/70 font-body mt-4 leading-relaxed">
                The control plane for CAs, admins, and super admins —
                workflows, clients, payments, and compliance, all in one place.
              </p>
            </div>

            {/* Rotating highlight */}
            <div
              key={highlightIdx}
              className="bg-white/[0.06] backdrop-blur-sm border border-white/10 rounded-2xl p-5 transition-all duration-500"
            >
              <div className="flex items-start gap-4">
                <div className="bg-white/10 rounded-xl p-2.5 flex-shrink-0">
                  <HighlightIcon className="h-5 w-5 text-[#A5B4FC]" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-body font-semibold tracking-[0.2em] uppercase text-[#A5B4FC]">
                    {highlight.eyebrow}
                  </p>
                  <p className="text-base font-heading font-semibold mt-1">
                    {highlight.title}
                  </p>
                  <p className="text-sm text-white/65 font-body mt-1.5 leading-relaxed">
                    {highlight.body}
                  </p>
                </div>
              </div>
            </div>

            {/* Pagination dots */}
            <div className="flex items-center gap-1.5">
              {HIGHLIGHTS.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setHighlightIdx(i)}
                  aria-label={`Highlight ${i + 1}`}
                  className={`h-1 rounded-full transition-all ${
                    i === highlightIdx ? "w-8 bg-white" : "w-1.5 bg-white/30 hover:bg-white/50"
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Trust footer */}
          <div className="flex items-center gap-6 text-xs font-body text-white/60">
            <div className="flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5" />
              Encrypted session
            </div>
            <div className="flex items-center gap-1.5">
              <Lock className="h-3.5 w-3.5" />
              Audit-logged
            </div>
            <div className="flex items-center gap-1.5">
              <Users className="h-3.5 w-3.5" />
              Role-based access
            </div>
          </div>
        </div>
      </div>

      {/* ─── Right: sign-in card ───────────────────────────────────────── */}
      <div className="flex-1 flex items-center justify-center px-5 py-10 lg:px-12">
        <div className="w-full max-w-md">
          {/* Mobile logo (left panel is hidden on small screens) */}
          <div className="lg:hidden flex justify-center mb-8">
            <img
              src="/myca-logo.png"
              alt="myCA"
              className="h-12 w-auto object-contain"
            />
          </div>

          <div className="bg-white rounded-3xl border border-[#E8E6F8] shadow-[0_24px_60px_-30px_rgba(108,99,255,0.35)] p-8 sm:p-10">
            {success ? (
              /* ─── Success confirmation ──────────────────────────────── */
              <div className="flex flex-col items-center text-center py-6">
                <div className="relative mb-6">
                  <span className="absolute inset-0 rounded-full bg-emerald-400/30 animate-ping" />
                  <div className="relative bg-emerald-50 rounded-full p-3">
                    <CheckCircle2 className="h-12 w-12 text-emerald-500" strokeWidth={1.75} />
                  </div>
                </div>
                <h2 className="text-2xl font-heading font-bold text-[#1A1A2E] tracking-tight">
                  Signed in!
                </h2>
                <p className="text-sm text-[#6B7280] font-body mt-1.5">
                  Redirecting you to your workspace…
                </p>
                <div className="w-full mt-7 h-1.5 rounded-full bg-[#EEF0F8] overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-[#6C63FF] to-[#8B7EFF] animate-[signin-progress_1.1s_ease-in-out_forwards]" />
                </div>
              </div>
            ) : (
            <>
            <div className="mb-7">
              <h2 className="text-2xl sm:text-[28px] font-heading font-bold text-[#1A1A2E] tracking-tight">
                Welcome back
              </h2>
              <p className="text-sm text-[#6B7280] font-body mt-1.5">
                Sign in to continue. CA, Admin, or Super Admin — same door.
              </p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 p-3 rounded-xl mb-5 text-sm font-body flex items-start gap-2">
                <span className="leading-tight">{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-body font-medium text-[#4A4A60] mb-1.5">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF] pointer-events-none" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    autoComplete="email"
                    placeholder="you@myca.pro"
                    className="w-full h-12 pl-10 pr-3.5 rounded-xl border border-[#E8E6F8] bg-[#FAFAFD] text-[#1A1A2E] placeholder:text-[#9CA3AF] text-sm font-body focus:outline-none focus:ring-2 focus:ring-[#6C63FF]/25 focus:border-[#6C63FF] focus:bg-white transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-body font-medium text-[#4A4A60] mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF] pointer-events-none" />
                  <input
                    type={showPwd ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    placeholder="••••••••"
                    className="w-full h-12 pl-10 pr-11 rounded-xl border border-[#E8E6F8] bg-[#FAFAFD] text-[#1A1A2E] placeholder:text-[#9CA3AF] text-sm font-body focus:outline-none focus:ring-2 focus:ring-[#6C63FF]/25 focus:border-[#6C63FF] focus:bg-white transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((s) => !s)}
                    aria-label={showPwd ? "Hide password" : "Show password"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#6C63FF] transition-colors"
                  >
                    {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="group w-full h-12 mt-2 bg-gradient-to-r from-[#6C63FF] to-[#8B7EFF] hover:from-[#5B52CC] hover:to-[#7A6BFF] disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-xl text-sm font-body font-semibold shadow-[0_10px_24px_-12px_rgba(108,99,255,0.55)] transition-all flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Signing In…
                  </>
                ) : (
                  <>
                    Sign In
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                  </>
                )}
              </button>
            </form>

            <div className="mt-7 pt-5 border-t border-[#E8E6F8]">
              <p className="text-xs text-[#9CA3AF] font-body text-center leading-relaxed">
                Forgot your password? Contact a Super Admin to reset it.
                <br />
                <span className="text-[#6B7280]">
                  Sign-in attempts are logged with IP and user-agent.
                </span>
              </p>
            </div>
            </>
            )}
          </div>

          <p className="text-center text-xs text-[#9CA3AF] font-body mt-6">
            © {new Date().getFullYear()} myCA · Authorised personnel only
          </p>
        </div>
      </div>
    </div>
  );
}
